import { useApp } from "@/context/AppContext";
import { useSEO } from "@/hooks/useSEO";
import { ArrowUpRight, Mail, Phone, Clock } from "lucide-react";
import { KEYWORDS } from "@/lib/seoContent";
import { webPageLd, breadcrumbLd, organizationLd } from "@/lib/structuredData";

const EMAIL = "netovibe@outlook.com";
const PHONE_DISPLAY = "+49 155 6944 5772";
const PHONE_TEL = "+4915569445772";
const WHATSAPP_DISPLAY = "+49 155 6327 1439";
const WHATSAPP = "4915563271439";

const Contact = () => {
  const { t, lang } = useApp();
  useSEO({
    title: t.contact.metaTitle,
    description: t.contact.metaDesc,
    path: "/contact",
    lang,
    keywords: KEYWORDS[lang].contact,
    jsonLd: [
      organizationLd(),
      webPageLd({
        path: "/contact",
        name: t.contact.metaTitle,
        description: t.contact.metaDesc,
        lang,
        type: "ContactPage",
      }),
      breadcrumbLd([
        { name: t.nav.home, path: "/" },
        { name: t.nav.contact, path: "/contact" },
      ]),
    ],
  });

  const responseLabel = lang === "de" ? "Antwortzeit" : "Response time";
  const responseValue = lang === "de" ? "innerhalb eines Werktags" : "within 1 business day";
  const emailLabel = lang === "de" ? "E-Mail schreiben" : "Send an email";
  const phoneLabel = lang === "de" ? "Jetzt anrufen" : "Call directly";
  const whatsappLabel = lang === "de" ? "WhatsApp Chat starten" : "Chat on WhatsApp";
  const preferLabel =
    lang === "de"
      ? "Am liebsten telefonisch — direkt, persönlich, schnell."
      : "We prefer a quick call — direct, personal, no forms.";
  const kicker =
    lang === "de"
      ? "Zwei Wege. Beide führen zu einem echten Menschen."
      : "Two ways. Both go to a real human.";

  return (
    <main data-testid="contact-page" className="pt-16 lg:pt-20">
      <section>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left — big statement */}
          <aside className="lg:col-span-6">
            <div className="lg:sticky lg:top-28">
              <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
                <span className="inline-block h-[1px] w-8 bg-[#3A00FF]" />
                {t.contact.eyebrow}
              </div>
              <h1
                data-testid="contact-title"
                className="mt-6 text-5xl sm:text-6xl md:text-7xl lg:text-[7rem] leading-[0.9] tracking-tighter font-black text-[#0A0A0A] font-[Cabinet_Grotesk]"
              >
                {t.contact.title}
              </h1>
              <p className="mt-8 max-w-md text-base sm:text-lg text-[#525252]">
                {t.contact.lead}
              </p>

              <div className="mt-10 flex items-start gap-3 text-sm text-[#525252]">
                <Clock className="h-4 w-4 mt-0.5 text-[#3A00FF]" aria-hidden />
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
                    {responseLabel}
                  </div>
                  <div className="mt-1">{responseValue}</div>
                </div>
              </div>
            </div>
          </aside>

          {/* Right — direct contact cards */}
          <div className="lg:col-span-6">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#3A00FF]">
              {kicker}
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5">
              {/* PHONE — primary, inverted */}
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="contact-phone-card"
                className="group relative block border border-[#0A0A0A] bg-[#0A0A0A] text-white p-6 sm:p-8 shadow-[6px_6px_0px_#3A00FF] hover:shadow-[10px_10px_0px_#3A00FF] hover:-translate-x-[2px] hover:-translate-y-[2px] transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#3A00FF]">
                      <Phone className="h-3.5 w-3.5" aria-hidden />
                      {phoneLabel}
                    </div>
                    <div
                      data-testid="contact-phone-number"
                      className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight font-[Cabinet_Grotesk] break-all"
                    >
                      {PHONE_DISPLAY}
                    </div>
                    <div className="mt-3 text-sm text-white/60">
                      {preferLabel}
                    </div>
                  </div>
                  <ArrowUpRight
                    className="h-6 w-6 shrink-0 text-white group-hover:text-[#3A00FF] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
                    aria-hidden
                  />
                </div>
              </a>

              {/* EMAIL */}
              <a
                href={`mailto:${EMAIL}?subject=${encodeURIComponent(
                  lang === "de" ? "Angebot anfordern" : "Request a quote",
                )}`}
                data-testid="contact-email-card"
                className="group relative block border border-[#0A0A0A] bg-white text-[#0A0A0A] p-6 sm:p-8 shadow-[6px_6px_0px_#0A0A0A] hover:shadow-[10px_10px_0px_#0A0A0A] hover:-translate-x-[2px] hover:-translate-y-[2px] transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#3A00FF]">
                      <Mail className="h-3.5 w-3.5" aria-hidden />
                      {emailLabel}
                    </div>
                    <div
                      data-testid="contact-email-address"
                      className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight font-[Cabinet_Grotesk] break-all"
                    >
                      {EMAIL}
                    </div>
                    <div className="mt-3 text-sm text-[#525252]">
                      {lang === "de"
                        ? "Kurz Projekt, Umfang und Deadline nennen — Sie bekommen ein transparentes Angebot zurück."
                        : "Tell us the project, scope, and any deadline — you'll get a transparent quote back."}
                    </div>
                  </div>
                  <ArrowUpRight
                    className="h-6 w-6 shrink-0 group-hover:text-[#3A00FF] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
                    aria-hidden
                  />
                </div>
              </a>

              {/* WHATSAPP — tertiary */}
              <a
                href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
                  lang === "de"
                    ? "Hallo NetoVibe, ich hätte gern ein Angebot für ein Website-Projekt."
                    : "Hi NetoVibe, I'd like a quote for a website project.",
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-card"
                className="group relative block border border-[#0A0A0A] bg-white text-[#0A0A0A] px-6 py-5 hover:bg-[#3A00FF] hover:text-white transition-colors"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold">
                    <span className="inline-block h-2 w-2 bg-[#3A00FF] group-hover:bg-white" />
                    {whatsappLabel}
                  </div>
                  <span className="text-sm font-bold tracking-tight">
                    {WHATSAPP_DISPLAY}
                  </span>
                </div>
              </a>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 border-t border-l border-[#0A0A0A]/20">
              {t.contact.details.map((d, i) => (
                <div
                  key={i}
                  className="p-4 sm:p-5 border-r border-b border-[#0A0A0A]/20"
                >
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
                    {d.k}
                  </div>
                  <div className="mt-2 text-sm text-[#525252]">{d.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
